export type FetchWithRetryOptions = {
  /** Nombre de tentatives supplementaires apres le premier essai. */
  retries?: number;
  /** Delai de repli (ms) quand le serveur ne renvoie pas d'en-tete Retry-After. */
  fallbackDelayMs?: number;
};

const delay = (ms: number): Promise<void> => new Promise((resolve) => { setTimeout(resolve, ms); });

/**
 * Convertit un en-tete Retry-After (secondes ou date HTTP) en millisecondes.
 */
const parseRetryAfter = (headerValue: string | null): number | null => {
  if (!headerValue) {
    return null;
  }
  const seconds = Number(headerValue);
  if (!Number.isNaN(seconds)) {
    return Math.max(0, seconds * 1000);
  }
  const dateMs = Date.parse(headerValue);
  if (!Number.isNaN(dateMs)) {
    return Math.max(0, dateMs - Date.now());
  }
  return null;
};

// Reference vers le fetch natif, capturee au moment de l'installation de
// l'intercepteur global. Permet a fetchWithRetry de ne pas re-wrapper le fetch
// deja wrappe (sinon on retry-erait le retry).
let nativeFetch: typeof fetch | null = null;

/**
 * Boucle de retry sur les 429, parametree par l'implementation de fetch a utiliser.
 */
const runWithRetry = async(
  fetchImpl: typeof fetch,
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  options: FetchWithRetryOptions,
): Promise<Response> => {
  const { retries = 3, fallbackDelayMs = 1000 } = options;

  let attempt = 0;
  while (true) {
    // eslint-disable-next-line no-await-in-loop -- sequential by design: we retry the same request after a backoff
    const response = await fetchImpl(input, init);
    if (response.status !== 429 || attempt >= retries) {
      return response;
    }
    const retryAfterMs = parseRetryAfter(response.headers.get('Retry-After'));
    // eslint-disable-next-line no-await-in-loop -- sequential by design: wait for the Retry-After delay before retrying
    await delay(retryAfterMs ?? fallbackDelayMs * (attempt + 1));
    attempt += 1;
  }
};

/**
 * fetch avec retry automatique sur les reponses 429 (rate limit), en respectant
 * l'en-tete Retry-After quand il est present. Utile pour les endpoints proteges
 * par le rate limit nginx (zone api_limit).
 */
export const fetchWithRetry = (
  input: RequestInfo | URL,
  init?: RequestInit,
  options: FetchWithRetryOptions = {},
): Promise<Response> => runWithRetry(nativeFetch ?? globalThis.fetch.bind(globalThis), input, init, options);

/**
 * Installe un intercepteur global qui ajoute le retry 429/Retry-After a TOUS les
 * appels fetch de l'app. Comme ca, meme un endpoint auquel on n'a pas pense se
 * fera retry automatiquement en cas de rate limit. Idempotent.
 */
export const installFetchRetryInterceptor = (options: FetchWithRetryOptions = {}): void => {
  if (typeof globalThis === 'undefined' || typeof globalThis.fetch !== 'function' || null !== nativeFetch) {
    return;
  }
  nativeFetch = globalThis.fetch.bind(globalThis);
  const original = nativeFetch;
  globalThis.fetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => runWithRetry(original, input, init, options);
};
